import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowRight, Mail, Clock, MapPin, Sun, Moon } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, WEBSITE, AREAS } from "@/data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/estimate", label: "Contact" },
];

const ThemeToggle = ({ className = "" }) => {
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    try { localStorage.setItem("mtc-theme", next ? "dark" : "light"); } catch {}
  };
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      className={`p-2.5 border border-obsidian/25 text-obsidian hover:bg-obsidian hover:text-bone transition-colors duration-300 ${className}`}
    >
      {dark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
};

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center">
    <img src="/logo.png" alt="MTC Home Renovation" className="site-logo h-9 sm:h-11 w-auto" />
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-obsidian text-bone/80">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between h-9 text-[11px] font-mono uppercase tracking-[0.18em]">
          <a href={PHONE_TEL} data-testid="topbar-call-link" className="flex items-center gap-2 text-ambercraft font-semibold hover:text-bone transition-colors">
            <Phone size={11} /> {PHONE_DISPLAY}
          </a>
          <span className="hidden md:block">Licensed & Insured · Serving the GTA</span>
        </div>
      </div>
      <div className="bg-bone/90 backdrop-blur-md border-b border-obsidian/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 sm:h-[72px]">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-teak" : "text-obsidian/70 hover:text-obsidian"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle />
            <a
              href={PHONE_TEL}
              data-testid="header-call-now-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border border-obsidian/25 text-obsidian hover:bg-obsidian hover:text-bone transition-colors duration-300"
            >
              <Phone size={14} /> Call Now
            </a>
            <Link
              to="/estimate"
              data-testid="header-book-button"
              className="group inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-teak text-bone hover:bg-teakdark transition-colors duration-300"
            >
              Book
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="p-2 text-obsidian"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-bone border-b border-obsidian/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="px-5 py-4 flex flex-col">
              {NAV.map((n) => (
                <NavLink
                  key={n.label}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3.5 text-lg font-display border-b border-obsidian/10 ${isActive ? "text-teak" : "text-obsidian"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/estimate"
                data-testid="mobile-nav-book"
                className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-teak text-bone font-semibold text-sm"
              >
                Book <ArrowRight size={15} />
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="mobile-call-now-button"
                className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-obsidian text-bone font-semibold text-sm"
              >
                <Phone size={15} /> Call {PHONE_DISPLAY}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-obsidian text-bone" data-testid="site-footer">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
      <div>
        <img src="/logo.png" alt="MTC Home Renovation" className="h-12 w-auto" data-testid="footer-logo" />
        <p className="mt-5 text-sm text-bone/60 leading-relaxed max-w-xs">
          Quality home renovations across the Greater Toronto Area — kitchens, bathrooms, basements, flooring and complete transformations.
        </p>
        <a href="https://mtcrenovation.ca" data-testid="footer-website-link" className="mt-4 inline-block text-sm text-teak hover:text-ambercraft transition-colors">
          {WEBSITE}
        </a>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-teak mb-5">Explore</p>
        <ul className="space-y-3 text-sm">
          {[...NAV.slice(0, 4), { to: "/estimate", label: "Book" }].map((n) => (
            <li key={n.label}>
              <Link to={n.to} data-testid={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-bone/60 hover:text-bone transition-colors">
                {n.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-teak mb-5">Contact</p>
        <ul className="space-y-3 text-sm text-bone/60">
          <li>
            <a href={PHONE_TEL} data-testid="footer-phone-link" className="flex items-center gap-2 text-bone font-semibold hover:text-teak transition-colors">
              <Phone size={14} className="text-teak" /> {PHONE_DISPLAY}
            </a>
          </li>
          <li>
            <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="flex items-center gap-2 hover:text-bone transition-colors">
              <Mail size={14} className="text-teak" /> {EMAIL}
            </a>
          </li>
          <li className="flex items-center gap-2"><Clock size={14} className="text-teak" /> Mon – Sat: 7:30 AM – 6:30 PM</li>
          <li className="flex items-start gap-2"><MapPin size={14} className="text-teak mt-0.5" /> Serving the Greater Toronto Area</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-bone/10">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-bone/40">
        <span>© {new Date().getFullYear()} MTC Home Renovation · {WEBSITE}</span>
        <span>Licensed & Insured · {AREAS[0]} & the GTA</span>
      </div>
    </div>
  </footer>
);

const MobileCTA = () => (
  <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden grid grid-cols-2 border-t border-obsidian/10" data-testid="mobile-sticky-cta">
    <a href={PHONE_TEL} data-testid="sticky-call-button" className="flex items-center justify-center gap-2 bg-obsidian text-bone py-4 text-sm font-semibold">
      <Phone size={15} /> Call Now
    </a>
    <Link to="/estimate" data-testid="sticky-book-button" className="flex items-center justify-center gap-2 bg-teak text-bone py-4 text-sm font-semibold">
      Book <ArrowRight size={15} />
    </Link>
  </div>
);

export default function Layout() {
  return (
    <div className="grain min-h-screen bg-bone text-obsidian">
      <Header />
      <main className="pb-16 lg:pb-0">
        <Outlet />
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}
